export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "/api/v1";

export const USER_STORAGE_KEY = "coffee_pos_user";

export const QUERY_KEYS = {
  currentUser: ["auth", "me"],
  users: ["users"],
  user: (id: string) => ["users", id],
  roles: ["roles"],
  role: (id: string) => ["roles", id],
  permissions: ["permissions"],

  products: ["products"],
  allProducts: ["products", "all"],
  product: (id: string) => ["products", id],
  categories: ["categories"],
  category: (id: string) => ["categories", id],
  attributes: ["attributes"],
  attribute: (id: string) => ["attributes", id],

  orders: (filters?: Record<string, unknown>) => ["orders", filters],
  activeOrders: ["orders", { status: "active" }],
  order: (id: string) => ["orders", id],
  orderInvoice: (id: string) => ["orders", id, "invoice"],

  customers: ["customers"],
  customer: (id: string) => ["customers", id],
  customerLookup: (phone: string) => ["customers", "lookup", phone],

  inventory: ["inventory"],
  inventoryItem: (id: string) => ["inventory", id],
  stockMovements: (itemId?: string) => ["inventory", "movements", itemId],
  lowStock: ["inventory", "low-stock"],
  inventoryTransactions: ["inventory", "transactions"],

  shifts: ["shifts"],
  shift: (id: string) => ["shifts", id],
  currentShift: ["shifts", "current"],
  shiftSummary: (id: string) => ["shifts", id, "summary"],

  settings: ["settings"],
  setting: (key: string) => ["settings", key],

  dashboardReport: ["reports", "dashboard"],
  dashboard: (params?: Record<string, unknown>) => [
    "reports",
    "dashboard",
    params,
  ],
  salesReport: (startDate?: string, endDate?: string) => [
    "reports",
    "sales",
    { startDate, endDate },
  ],
  productReport: (startDate?: string, endDate?: string) => [
    "reports",
    "products",
    { startDate, endDate },
  ],
  paymentReport: (startDate?: string, endDate?: string) => [
    "reports",
    "payments",
    { startDate, endDate },
  ],
  endOfDayReport: (date?: string) => ["reports", "end-of-day", date],
  topSellers: (params?: Record<string, unknown>) => [
    "reports",
    "top-sellers",
    params,
  ],
  endOfDay: (date: string) => ["reports", "end-of-day", date],

  timeClockStatus: ["timeclock", "status"],
  timeClockEntries: ["timeclock", "entries"],
  timeClockSummary: (params?: Record<string, unknown>) => [
    "timeclock",
    "summary",
    params,
  ],

  refunds: ["refunds"],
  refund: (id: string) => ["refunds", id],
  orderRefunds: (orderId: string) => ["refunds", "order", orderId],

  favorites: ["favorites"],
  favorite: (id: string) => ["favorites", id],

  promotions: ["promotions"],
  promotion: (id: string) => ["promotions", id],
  activeHappyHours: ["promotions", "happy-hours", "active"],

  auditLogs: (filters?: Record<string, unknown>) => ["audit", filters],
} as const;

export const ORDER_STATUSES = {
  pending: { label: "Pending", color: "yellow" },
  preparing: { label: "Preparing", color: "blue" },
  ready: { label: "Ready", color: "green" },
  completed: { label: "Completed", color: "gray" },
  cancelled: { label: "Cancelled", color: "red" },
} as const;

export const PAYMENT_METHODS = {
  cash: { label: "Cash", icon: "Banknote" },
  card: { label: "Card", icon: "CreditCard" },
  mobile: { label: "Mobile", icon: "Smartphone" },
} as const;

export const USER_ROLES = {
  admin: { label: "Admin", color: "purple" },
  manager: { label: "Manager", color: "blue" },
  barista: { label: "Barista", color: "green" },
  cashier: { label: "Cashier", color: "orange" },
} as const;

export const INVENTORY_UNITS = {
  g: { label: "Grams", short: "g" },
  ml: { label: "Milliliters", short: "ml" },
  pieces: { label: "Pieces", short: "pcs" },
  oz: { label: "Ounces", short: "oz" },
} as const;

export const NAVIGATION_ITEMS = [
  { path: "/", label: "POS", icon: "ShoppingCart", permission: null },
  { path: "/orders", label: "Orders", icon: "ClipboardList", permission: null },
  { path: "/kitchen", label: "Kitchen", icon: "ChefHat", permission: null },
  {
    path: "/inventory",
    label: "Inventory",
    icon: "Package",
    permission: "inventory.read",
  },
  {
    path: "/customers",
    label: "Customers",
    icon: "Users",
    permission: "customers.read",
  },
  {
    path: "/shifts",
    label: "Shifts",
    icon: "Clock",
    permission: "shifts.read",
  },
  { path: "/timeclock", label: "Time Clock", icon: "Timer", permission: null },
  { path: "/refunds", label: "Refunds", icon: "RotateCcw", permission: null },
  { path: "/favorites", label: "Favorites", icon: "Heart", permission: null },
] as const;

export const REPORTS_NAVIGATION = [
  { path: "/reports", label: "Dashboard", icon: "BarChart3", permission: null },
  {
    path: "/reports/end-of-day",
    label: "End of Day",
    icon: "Calendar",
    permission: null,
  },
] as const;

export const ADMIN_NAVIGATION = [
  {
    path: "/admin/users",
    label: "Users",
    icon: "UserCog",
    permission: "users.read",
  },
  {
    path: "/admin/products",
    label: "Products",
    icon: "Coffee",
    permission: "products.read",
  },
  {
    path: "/admin/attributes",
    label: "Attributes",
    icon: "Settings2",
    permission: "products.read",
  },
  {
    path: "/admin/promotions",
    label: "Promotions",
    icon: "Tag",
    permission: null,
  },
  {
    path: "/admin/audit",
    label: "Audit Log",
    icon: "ScrollText",
    permission: null,
  },
  {
    path: "/settings",
    label: "Settings",
    icon: "Settings",
    permission: "settings.read",
  },
] as const;
